import { useState, useCallback, useEffect } from 'react'
import api from '../../../services/api'

const useAiCreditCredits = (initialFilter = '5') => {
  const [dateFilter, setDateFilter] = useState(initialFilter)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Credits table state
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [creditsData, setCreditsData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [tableFilter, setTableFilter] = useState('All Time')

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleDateFilterChange = useCallback(
    value => {
      setTableFilter(value)
      setOffset(0)
      if (value !== 'Other') {
        setDateFrom('')
        setDateTo('')
      }
    },
    []
  )

  const handleDateRangeChange = useCallback(
    (dates, dateStrings) => {
      if (dateStrings && Array.isArray(dateStrings) && dateStrings.length === 2) {
        setDateFrom(dateStrings[0])
        setDateTo(dateStrings[1])
        setOffset(0)
      } else if (dates && Array.isArray(dates) && dates.length === 2) {
        setDateFrom(dates[0] ? dates[0].format('YYYY-MM-DD') : '')
        setDateTo(dates[1] ? dates[1].format('YYYY-MM-DD') : '')
        setOffset(0)
      } else {
        setDateFrom('')
        setDateTo('')
      }
    },
    []
  )

  const handleSearch = useCallback((value) => {
    setSearchTerm(value)
    setOffset(0)
  }, [])

  const handlePageChange = useCallback((page, pageSize) => {
    setOffset((page - 1) * pageSize)
    setLimit(pageSize)
  }, [])

  const fetchCredits = useCallback(async () => {
    setLoading(true)
    setError(null)
    setCreditsData([])

    const mapFilter = (label) => {
      switch (label) {
        case 'All Time': return 'allTime'
        case 'Today': return 'today'
        case 'This Week': return 'week'
        case 'This Month': return 'month'
        case 'This Year': return 'year'
        case 'Other': return 'other'
        default: return ''
      }
    }

    const selectedDateFilter = mapFilter(tableFilter) ||
      (dateFilter === 'other' || dateFilter === '2' || dateFilter === '5' ? 'allTime' : dateFilter)

    if (selectedDateFilter === 'other' && (!dateFrom || !dateTo)) {
      setLoading(false)
      return
    }

    try {
      const response = await api.reports.getUsageReportCreditsService({
        offset,
        limit,
        search: debouncedSearchTerm,
        date: selectedDateFilter,
        from: dateFrom,
        to: dateTo
      })

      const responseData = response?.data || response
      if (responseData?.status === 'error') {
        throw new Error(responseData?.message || 'Failed to fetch credits report data')
      }
      const rawRecords = responseData?.users || []

      const mappedData = Array.isArray(rawRecords) ? rawRecords.map(item => {
        const userObj = item.creator
        const creatorName = userObj ? `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim() : null
        const displayAvatar = userObj?.avatar?.link || ''

        return {
          id: item.id || item.user_id || userObj?.id,
          name: creatorName,
          rank: userObj?.rank || '',
          avatar: displayAvatar,
          totalCreditsUsed: item.total_used_credits || 0,
        }
      }) : []

      setCreditsData(mappedData)
      setTotal(responseData.total || 0)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching credits report data:', err)
      setError(err.message || 'Failed to fetch data')
      setLoading(false)
    }
  }, [dateFilter, dateFrom, dateTo, debouncedSearchTerm, tableFilter, offset, limit])

  useEffect(() => {
    fetchCredits()
  }, [fetchCredits])

  return {
    tableFilter,
    setTableFilter: handleDateFilterChange,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    creditsData,
    creditsLoading: loading,
    creditsError: error,
    creditsPagination: {
      current: Math.floor(offset / limit) + 1,
      pageSize: limit,
      total: total
    },
    creditsSearchTerm: searchTerm,
    onCreditsSearch: handleSearch,
    onCreditsPageChange: handlePageChange,
    refetchCredits: fetchCredits,
    setDateRange: handleDateRangeChange
  }
}

export default useAiCreditCredits
